// Damage / repair modal. Appears after a raid ends if at least one
// building or farm plot was torched, and again if the player has a
// labor-repair in progress on boot (so they remember they're paused).
// Two buttons: pay coin for instant repair, or commit citizen labor
// (halts goon income for ~12s per damaged tile).

import { postMessage, resolveMessage } from './messageRail.js'
import { Events, on } from './eventBus.js'
import { RaidDamageConfig, MessageRailConfig } from '../config/balance.js'
import { damageCount, startPaidRepair, startLaborRepair, getRepair } from '../game/damageManager.js'
import { getWealth } from '../game/seasonManager.js'
import { formatCoins, cantAfford } from './format.js'
import { BUY_COINS_HTML, wireBuyCoins } from './buyCoinsLink.js'
import { pushLog } from './hud.js'
import { isOnTrip } from '../game/capitalTrip.js'

export function mountRepairPanel({ scene }) {
  // The panel is a single shared DOM overlay appended to body on demand.
  let overlay = null

  // A persistent REPAIR chip: if you choose "Repair later" (or miss the prompt) the damage must not strand you
  // with no way back to the repair choice. Shows only while real damage exists and no repair is running.
  const chip = document.createElement('button')
  chip.className = 'repair-chip'
  chip.type = 'button'
  document.body.appendChild(chip)
  let lastRepairN = -1
  const showRepairCard = (n) => show(n)
  const updateChip = () => {
    const n = damageCount()
    const show = n > 0 && !getRepair() && !isOnTrip()
    chip.classList.toggle('on', show)
    if (show) chip.innerHTML = `🔥 <b>${n}</b> ${n === 1 ? 'site' : 'sites'} damaged <span>REPAIR</span>`
    // the reminder rides the message rail: if it was ignored (bumped, closed, snoozed) it returns when the rail is quiet, until repaired
    if (show) { if (n !== lastRepairN) { lastRepairN = n; showRepairCard(n) } } else { lastRepairN = -1; resolveMessage('repair') }
  }
  chip.addEventListener('click', () => { const n = damageCount(); if (n > 0 && !getRepair()) show(n, true) })
  ;[Events.BuildingDamaged, Events.PlotDamaged, Events.RepairStart, Events.RepairComplete, Events.KingdomChange, Events.RaidEnd, Events.PlotChanged]
    .forEach((ev) => on(ev, updateChip))
  setInterval(updateChip, 2000) // also catches returning from the Capital
  setTimeout(updateChip, 800)

  // While you're away in the Capital the repair choice waits for the "welcome home" screen.
  on(Events.RaidEnd, () => { if (!isOnTrip()) maybeShow() })
  on(Events.RepairStart, () => close())
  on(Events.RepairComplete, ({ mode, count }) => {
    close()
    pushLog(`Repairs complete via ${mode} (${count} ${count === 1 ? 'site' : 'sites'}).`)
  })
  // If the player reloads mid-labor-repair, surface the panel so they see
  // the timer running. Slight delay so the rest of the HUD mounts first.
  setTimeout(() => { if (getRepair()) showProgress() }, 600)

  function maybeShow() {
    const n = damageCount()
    if (n === 0) return
    show(n)
  }

  // The repair choice: a card on the rail. Left unmade it comes back between other messages (MessageRailConfig.reappearMs);
  // "Later" puts it off for snoozeMs; a repair started, or everything repaired, resolves it. The REPAIR chip stays meanwhile.
  function show(n, now = false) {
    const cost = n * RaidDamageConfig.paidRepairCostPerDamage
    const laborMs = n * RaidDamageConfig.laborRepairMsPerDamage
    const laborMin = Math.ceil(laborMs / 60000)
    const canAfford = getWealth() >= cost
    postMessage({
      key: 'repair', kind: 'choice', type: 'REPAIR', icon: '🔥', now, title: 'The kingdom is burning',
      text: `The raid left ${n} ${n === 1 ? 'structure' : 'structures / fields'} torched. Damaged buildings give no bonus until repaired.${canAfford ? '' : ` You can't afford to pay yet (${formatCoins(cost - getWealth())} short) — use Citizen Labor.`}`,
      action: 'Pay to repair everything now, or dedicate citizens (cheap, but goon income halts until they finish).',
      choices: [
        { label: `Citizen Labor · ~${laborMin} min`, onPick: () => { if (startLaborRepair()) showProgress() } },
        { label: `Pay ${formatCoins(cost)} now`, primary: true, disabled: !canAfford, onPick: () => { if (!startPaidRepair()) pushLog(cantAfford('repair now', cost, getWealth())) } },
        { label: 'Later', snoozeMs: MessageRailConfig.snoozeMs },
      ],
    })
  }

  function showProgress() {
    close()
    const r = getRepair()
    if (!r) return
    overlay = document.createElement('div')
    overlay.className = 'reg__confirm'
    overlay.style.pointerEvents = 'none' // info-only, dismissible only on completion
    overlay.innerHTML = `
      <div class="reg__confirm-card" style="border-color:#c97a45;pointer-events:auto">
        <div class="reg__confirm-title" style="color:#ffa470">🛠 Citizen Labor In Progress</div>
        <div class="reg__confirm-body">
          Your citizens are rebuilding <strong>${r.totalDamages}</strong>
          ${r.totalDamages === 1 ? 'site' : 'sites'}. While they work, all
          goon income is paused.<br><br>
          <span data-repair-remaining>—</span>
        </div>
        <div class="reg__confirm-actions">
          <button class="cancel" data-repair-hide>Hide</button>
        </div>
      </div>
    `
    document.body.appendChild(overlay)
    overlay.querySelector('[data-repair-hide]').addEventListener('click', close)
    const tickEl = overlay.querySelector('[data-repair-remaining]')
    const tick = () => {
      const cur = getRepair()
      if (!cur) { close(); return }
      const remaining = Math.max(0, cur.endsAt - Date.now())
      const s = Math.ceil(remaining / 1000)
      tickEl.textContent = `Time remaining: ${s}s`
    }
    tick()
    const timer = setInterval(tick, 500)
    overlay.dataset.intervalId = String(timer)
  }

  function close() {
    if (overlay?.dataset?.intervalId) clearInterval(Number(overlay.dataset.intervalId))
    overlay?.remove()
    overlay = null
    resolveMessage('repair')
  }
}
